import { joiResolver } from '@hookform/resolvers/joi';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Container, FormControlLabel, Grid, Radio, RadioGroup, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { API, RPO_SEARCH_URL } from '../../../global/config/api';
import { USER_DATA } from '../../../global/config/constants';
import { getFilters } from '../../../global/helpers/filters';
import { useServerCall } from '../../../global/helpers/hooks';
import { isIBANValid } from '../../../global/helpers/validators';
import { getByKey } from '../../../global/storage/StorageEx';
import CodelistStore from '../../../global/stores/CodelistStore';
import GlobalStore from '../../../global/stores/GlobalStore';
import { IAttachment } from '../../../global/types/types';
import {
  BaseHeader,
  XsAutocompleteServer,
  XsAutocompleteServerWithoutLoad,
  XsButton,
  XsCheckbox,
  XsDatePicker,
  XsDropzone,
  XsHeader,
  XsIconButton,
  XsNumberField,
  XsSearchSelect,
  XsTextField
} from '../../../global/ui';
import { ICityRow, ICodelistRow, IDeviceRow } from '../../../swagger/gd/codelist';
import { ICompanyFlatData } from '../../../swagger/gd/company';
import {
  IGdRecordProxyOut,
  IGdRecordSave,
  IGdRecordSaveResponse,
  IOwnerData,
  IProjectImplementation
} from '../../../swagger/gd/record';
import { IRoundsRow } from '../../../swagger/gd/round';
import { ListResponse } from '../../../swagger/shared/list';
import { IUserSession } from '../../../swagger/shared/user';
import ConsentDialog from './ConsentDialog';
import ConsentDialogStore from './ConsentDialogStore';
import DeviceDialog from './DeviceDialog';
import DeviceDialogStore, { IDeviceEntry, IInstalledDeviceEntry, IPlaceEntry } from './DeviceDialogStore';
import InstalledDeviceSelectDialog, { IInstalledDeviceSelection } from './InstalledDeviceSelectDialog';
import RequestFormStore, {
  ATTACHMENT_KEYS,
  AttachmentKey,
  INSTALLED_ATTACHMENT_KEYS,
  InstalledAttachmentKey
} from './RequestFormStore';
import RequestFormStepper from './RequestFormStepper';
import RequestFormStepperStore, { STEP_COUNT } from './RequestFormStepperStore';
import RequestTypeDialogStore from './RequestTypeDialogStore';
import { IOwnerForm, IRequestForm, requestFormSchema } from './schema';
import WarningDialog from './WarningDialog';
import WarningDialogStore from './WarningDialogStore';

// Polia formulára, ktoré sa validujú v jednotlivých krokoch stepperu —
// uložiť/pokračovať na danom kroku nesmie zlyhať na poliach z neskorších krokov.
const STEP_FIELD_NAMES: Partial<Record<number, (keyof IRequestForm)[]>> = {
  0: [
    'ico',
    'dic',
    'icdph',
    'companyName',
    'companyLegalForm',
    'companyIBAN',
    'companySWIFT',
    'companySize',
    'skNace',
    'companyStreet',
    'companyCity',
    'contactPersonName',
    'contactPersonPhone',
    'contactPersonMail',
    'authorizedPersonName',
    'authorizedPersonPhone',
    'authorizedPersonMail',
    'communicationMethod',
    'numberOfOwners',
    'owners',
    'vatPayer'
  ],
  1: ['round_id'],
  2: [
    'singleEnterpriseNo',
    'singleEnterpriseYes',
    'illegalAidReturn',
    'excludedSectorsNo',
    'excludedSectorsYes',
    'otherMinAidNo',
    'otherMinAidYes'
  ],
  3: ['consentConditions']
};

// Prekladové kľúče názvov príloh — typové kódy GD:GDREQ:*
const ATTACHMENT_LABELS: Record<AttachmentKey, string> = {
  'GD:GDREQ:ENERGETICAUDIT': 'request_form.att_energetic_audit',
  'GD:GDREQ:OZE': 'request_form.att_oze',
  'GD:GDREQ:MINIMALDECLARATION': 'request_form.att_minimal_declaration',
  'GD:GDREQ:DIVISIONACTIVITY': 'request_form.att_division_activity',
  'GD:GDREQ:POWEROFATTORNEY': 'request_form.att_power_of_attorney',
  'GD:GDREQ:AFFIDAVIT': 'request_form.att_affidavit'
};

// "Prílohy žiadosti" v kroku 2 — doklady k už nainštalovanému zariadeniu (reimbursement tok)
const INSTALLED_ATTACHMENT_LABELS: Record<InstalledAttachmentKey, string> = {
  'GD:GDREQ:INSTALLATIONINVOICE': 'request_form.att_install_invoice',
  'GD:GDREQ:INSTALLATIONPAYMENT': 'request_form.att_payment_proof',
  'GD:GDREQ:AUDITORCONFIRMATION': 'request_form.att_auditor_confirmation'
};
const INSTALLED_ATTACHMENT_MAX_FILES: Record<InstalledAttachmentKey, number> = {
  'GD:GDREQ:INSTALLATIONINVOICE': 10,
  'GD:GDREQ:INSTALLATIONPAYMENT': 10,
  'GD:GDREQ:AUDITORCONFIRMATION': 1
};

const ALL_ATTACHMENT_KEYS: (AttachmentKey | InstalledAttachmentKey)[] = [
  ...ATTACHMENT_KEYS,
  ...INSTALLED_ATTACHMENT_KEYS
];

// ─── Tabuľka "Miesto realizácie projektu" — stĺpce zariadenia ────────────────
// Zoznam je dátovo riadený (nie pevný JSX), aby header a riadky ostali synchronizované.
type PlaceDeviceColumn = {
  key: string;
  labelKey: string;
  minWidth: number;
  align?: 'right';
  render: (device: IPlaceEntry['devices'][number]) => ReactNode;
};

const BASE_DEVICE_COLUMNS: PlaceDeviceColumn[] = [
  { key: 'device', labelKey: 'request_form.col_device', minWidth: 160, render: (d) => d.device },
  {
    key: 'power',
    labelKey: 'request_form.col_power',
    minWidth: 90,
    align: 'right',
    render: (d) => (d.power ? Number(d.power).toFixed(3).replace('.', ',') : '')
  },
  {
    key: 'capacity',
    labelKey: 'request_form.col_capacity',
    minWidth: 90,
    align: 'right',
    render: (d) => (d.capacity ? Number(d.capacity).toFixed(3).replace('.', ',') : '')
  }
];

// Doplnkové stĺpce pre tok "Zariadenie už mám nainštalované" (reimbursement)
const INSTALLED_DEVICE_COLUMNS: PlaceDeviceColumn[] = [
  // { key: 'catastral', labelKey: 'request_form.col_cadastral', minWidth: 110, render: (d) => d.catastral },
  // { key: 'parcel', labelKey: 'request_form.col_parcel', minWidth: 110, render: (d) => d.parcel },
  {
    key: 'deviceType',
    labelKey: 'request_form.col_device_type',
    minWidth: 140,
    render: (d) => d.deviceTypeName || '-'
  },
  {
    key: 'manufacturer',
    labelKey: 'request_form.col_manufacturer',
    minWidth: 130,
    render: (d) => d.manufacturer || '-'
  },
  { key: 'model', labelKey: 'request_form.col_model', minWidth: 140, render: (d) => d.modelType || '-' },
  {
    key: 'count',
    labelKey: 'request_form.col_count',
    minWidth: 70,
    align: 'right',
    render: (d) => d.count ?? ''
  },
  {
    key: 'power',
    labelKey: 'request_form.col_power',
    minWidth: 75,
    align: 'right',
    render: (d) => (d.power ? Number(d.power).toFixed(3).replace('.', ',') : '')
  },
  {
    key: 'attachments',
    labelKey: 'request_form.col_attachments',
    minWidth: 60,
    align: 'right',
    render: (d) => {
      const files = d.labelPhotos ?? [];
      return files.length === 0 ? <em style={{ color: 'gray' }}>–</em> : files.length;
    }
  }
];

// installed_devices_data (reimbursement) nemá rovnaký tvar ako devices_data — na vykreslenie
// cez INSTALLED_DEVICE_COLUMNS ho premapujeme na kompatibilný tvar (chýbajúce polia ako
// catastral/parcel/power jednoducho ostanú prázdne, keďže sa pri energetic-device nepoužívajú)
const toDisplayDevice = (entry: IInstalledDeviceEntry): IDeviceEntry => ({
  _id: entry._id,
  deviceId: entry.energeticDeviceId,
  deviceCode: '',
  device: entry.modelType ?? '',
  power: 0,
  deviceTypeName: entry.deviceTypeName,
  manufacturer: entry.manufacturer,
  modelType: entry.modelType,
  count: entry.count,
  labelPhotos: entry.attachments
});

const pad = (n: number) => String(n).padStart(2, '0');
const fmtDate = (s?: string): string => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const eur = (amount: number) =>
  new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(amount || 0);

// Label kola výzvy — verný port RequestFormStore.js getRounds()
const roundLabel = (o: IRoundsRow): string =>
  o?.region_type_code_ext
    ? `${o.region_type_code_ext} + ${o.devide_group_name_ext ?? ''} ${fmtDate(o.validity_from)} - ${fmtDate(
        o.validity_to
      )}`
    : (o?.round_id ?? '');

type OwnerFieldName = `owners.${number}.${keyof IOwnerForm}`;

const getCompanyId = (): string | undefined => {
  const user = getByKey(USER_DATA) as IUserSession | undefined;
  return user?.company_data?.company_id ?? undefined;
};

function RequestForm() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const id = new URLSearchParams(location.search).get('id') ?? undefined;

  const user = getByKey(USER_DATA) as IUserSession | undefined;
  const communicationElectronic = !!user?.company_data?.communication_method;

  const form = useForm<IRequestForm>({
    resolver: joiResolver(requestFormSchema),
    defaultValues: {
      ico: '',
      dic: '',
      icdph: '',
      companyName: '',
      companyLegalForm: '',
      companyIBAN: '',
      companySWIFT: '',
      companySize: '',
      skNace: '',
      companyStreet: '',
      companyCity: '',
      contactPersonName: '',
      contactPersonPhone: '',
      contactPersonMail: '',
      authorizedPersonName: '',
      authorizedPersonPhone: '',
      authorizedPersonMail: '',
      round_id: '',
      region_type_id: '',
      round_text: '',
      contributionForEnergyAudit: false,
      applicantChipForm: '',
      consent: false,
      consentConditions: false,
      vatPayer: false,
      singleEnterpriseNo: false,
      singleEnterpriseYes: false,
      illegalAidReturn: false,
      excludedSectorsNo: false,
      excludedSectorsYes: false,
      otherMinAidNo: false,
      otherMinAidYes: false,
      communicationMethod: communicationElectronic ? 'electronicCommunication' : 'writtenCommunication',
      numberOfOwners: 0,
      owners: []
    }
  });
  const { control, handleSubmit, reset, watch, setValue, getValues, trigger } = form;
  const {
    fields: ownerFields,
    append: appendOwner,
    remove: removeOwner
  } = useFieldArray({
    control,
    name: 'owners'
  });
  const currentStep = RequestFormStepperStore.currentStep;
  const continueAfterSaveRef = useRef(false);
  // Nová žiadosť (bez id v URL) — po prvom uložení si zapamätá _id z odpovede a posiela ho
  // ako record_id pri každom ďalšom kroku, aby ďalšie uloženia aktualizovali ten istý záznam
  // namiesto vytvárania nových.
  const savedRecordIdRef = useRef<string | undefined>(id);

  // Statutári — lokálny stav (v schéme je applicantChipForm ako JSON string)
  const [statutories, setStatutories] = useState<string[]>(['']);
  // Podniky tvoriace jediný podnik so žiadateľom — lokálny stav (rovnaká logika ako statutories)
  // Riadky majú stabilné id (nie index) — XsAutocompleteServerWithoutLoad si po prvom výbere
  // interne "zamkne" hodnotu a prestane sa synchronizovať s props.value, takže pri mazaní podľa
  // indexu by sa posunuté riadky zobrazovali s cudzími (starými) dátami.
  const rpoSearchNextId = useRef(1);
  const [rpoSearchCompanies, setRpoSearchCompanies] = useState<
    Array<{ id: number; value: { identifier: string; name: string } | null }>
  >([{ id: 0, value: null }]);
  // Iná minimálna pomoc (3.4, druhý checkbox) — lokálny stav, rovnaká logika ako statutories/rpoSearchCompanies
  const otherMinAidNextId = useRef(1);
  const [otherMinAidEntries, setOtherMinAidEntries] = useState<
    Array<{ id: number; provider: string; amount: string; date: Date | null }>
  >([{ id: 0, provider: '', amount: '', date: null }]);
  // Stav na zobrazenie / odoslanie
  const [pendingStatus, setPendingStatus] = useState<'inp' | 'sent' | 'valid'>('inp');
  const [gdRequestId, setGdRequestId] = useState('');
  const [roundText, setRoundText] = useState('');
  const [companyCityName, setCompanyCityName] = useState('');
  const [loadedVoucher, setLoadedVoucher] = useState<number | null>(null);
  const [loadedStatusId, setLoadedStatusId] = useState('');
  // Miesto, pre ktoré sa práve cez "Doplniť inštalované zariadenia" otvoril DeviceDialog
  // v dočasnom "reimbursement" režime — po zatvorení dialógu sa podľa neho vykoná konverzia.
  const installDevicesPlaceRef = useRef<IPlaceEntry | null>(null);
  // Riadok inštalovaného zariadenia práve editovaný cez InstalledDeviceSelectDialog (tlačidlo
  // "pen" v tabuľke miest nižšie) — po potvrdení sa nahradí novou selekciou (viď handleEditInstalledDeviceConfirm).
  const [editInstalledTarget, setEditInstalledTarget] = useState<{
    place: IPlaceEntry;
    entry: IInstalledDeviceEntry;
  } | null>(null);

  const readonly = RequestFormStore.readonlyForm;
  // Žiadosť bez inštalácie (rezervácia) v stave "potvrdená rezervácia" — jediná editovateľná
  // vec v kroku 2 (Údaje o projekte) je tlačidlo "Doplniť inštalované zariadenia" pri každom
  // mieste realizácie (všetko ostatné zostáva readonly cez RequestFormStore.readonlyForm).
  const canAddInstalledDevices = loadedStatusId === 'conf-reserv' && RequestFormStore.requestType !== 'reimbursement';

  const contributionForEnergyAudit = watch('contributionForEnergyAudit');
  const authorizedPersonName = watch('authorizedPersonName');
  const roundVal = watch('round_id');
  const hasRound = !!(roundVal && (typeof roundVal === 'object' ? roundVal.round_id : roundVal));

  // "Zariadenie už mám nainštalované" (reimbursement) — rozšíri tabuľku miest o stĺpce zariadenia.
  // Kým je cez "Doplniť inštalované zariadenia" otvorený DeviceDialog (installDevicesPlaceRef nastavený),
  // requestType je len dočasne prepnutý na 'reimbursement' pre samotný dialóg — tabuľka v kroku 2 sa
  // nemá zmeniť, kým používateľ dialóg nevyplní a neuloží.
  const showInstalledDeviceColumns =
    RequestFormStore.requestType === 'reimbursement' && !installDevicesPlaceRef.current;
  // Spoločná CSS grid šablóna pre dátové stĺpce — použitá identicky v hlavičke aj v každom riadku,
  // takže sa stĺpce medzi sebou nerozchádzajú (predtým samostatný flex na riadok → "utekajúce" dáta)
  const gridTemplate = (cols: PlaceDeviceColumn[]) => cols.map((col) => `minmax(${col.minWidth}px, 1fr)`).join(' ');
  const gridMinWidth = (cols: PlaceDeviceColumn[]) =>
    cols.reduce((sum, col) => sum + col.minWidth, 0) + (cols.length - 1) * 16;
  const baseGridTemplate = gridTemplate(BASE_DEVICE_COLUMNS);
  const baseGridMinWidth = gridMinWidth(BASE_DEVICE_COLUMNS);
  const installedGridTemplate = gridTemplate(INSTALLED_DEVICE_COLUMNS);
  const installedGridMinWidth = gridMinWidth(INSTALLED_DEVICE_COLUMNS);
  // Miesta s rezervovanými zariadeniami (devices_data) vs. miesta doplnené o nainštalované
  // zariadenia (installed_devices_data) — pri conf-reserv toku ("Doplniť inštalované zariadenia")
  // môžu v zozname existovať oba typy naraz, preto sa vykresľujú ako dve samostatné tabuľky.
  const placesWithDevices = DeviceDialogStore.places.filter((p) => p.devices.length > 0);
  const placesWithInstalled = DeviceDialogStore.places.filter((p) => p.installedDevices.length > 0);
  // Sekcie viazané na "nainštalované zariadenia" (tabuľka + prílohy nižšie) — zobrazené pri
  // reálnom reimbursement toku ALEBO akonáhle existuje aspoň jedno konvertované miesto (conf-reserv
  // tok), nezávisle od dočasného requestType prepnutého len počas otvoreného DeviceDialogu.
  const showInstalledSection = showInstalledDeviceColumns || placesWithInstalled.length > 0;

  /** Otvorí InstalledDeviceSelectDialog predvyplnený existujúcim riadkom (tlačidlo "pen" nižšie). */
  const openEditInstalledDevice = (place: IPlaceEntry, entry: IInstalledDeviceEntry) => {
    DeviceDialogStore.setRegionTypeId(String(getValues('region_type_id') ?? ''));
    const rid = getValues('round_id');
    DeviceDialogStore.setRoundId(String((typeof rid === 'object' && rid ? (rid as IRoundsRow).round_id : rid) ?? ''));
    const roundRow: IRoundsRow | undefined =
      typeof rid === 'object' && rid
        ? (rid as IRoundsRow)
        : RequestFormStore.roundsData.find((x) => x.round_id === rid);
    DeviceDialogStore.setDeviceGroupId(roundRow?.devide_group_id ?? '');
    setEditInstalledTarget({ place, entry });
  };

  // Potvrdenie editácie inštalovaného zariadenia (viď openEditInstalledDevice) — nahradí pôvodný
  // záznam v mieste novou selekciou, _id ostáva zachované.
  const handleEditInstalledDeviceConfirm = (selection: IInstalledDeviceSelection) => {
    if (!editInstalledTarget) return;
    const { place, entry } = editInstalledTarget;
    DeviceDialogStore.updateInstalledDevice(place, entry._id, {
      energeticDeviceId: selection.device._id,
      count: selection.count,
      attachments: selection.labelPhotos,
      deviceTypeName: selection.device.device_type_name,
      manufacturer: selection.device.manufacturer,
      modelType: selection.device.model_type,
      typeCode: selection.deviceTypeCode,
      specificationId: selection.specificationId,
      manufacturerCode: selection.manufacturerCode
    });
    setEditInstalledTarget(null);
  };

  const editInstalledInitial = editInstalledTarget
    ? {
        typeCode: editInstalledTarget.entry.typeCode ?? '',
        specificationId: editInstalledTarget.entry.specificationId ?? '',
        manufacturerCode: editInstalledTarget.entry.manufacturerCode ?? '',
        energeticDeviceId: editInstalledTarget.entry.energeticDeviceId,
        modelName: editInstalledTarget.entry.modelType ?? '',
        count: editInstalledTarget.entry.count,
        labelPhotos: editInstalledTarget.entry.attachments ?? []
      }
    : null;

  /** Vykreslí jeden riadok miesta (hlavička miesta + akcie + dátové riadky) v niektorej z dvoch tabuliek. */
  const renderPlaceBlock = (
    place: IPlaceEntry,
    columns: PlaceDeviceColumn[],
    gridTpl: string,
    gridMinW: number,
    kind: 'devices' | 'installed'
  ) => {
    // V conf-reserv toku je celý formulár readonly, no tabuľka inštalovaných zariadení (doplnená
    // cez "Doplniť inštalované zariadenia" — viď canAddInstalledDevices) musí ostať upraviteľná
    // rovnako ako samotné tlačidlo pridania, inak by pridaný riadok nešlo ani upraviť, ani zmazať.
    const rowActionsEnabled = kind === 'installed' ? !readonly || canAddInstalledDevices : !readonly;
    const rows: { key: string; content: IPlaceEntry['devices'][number]; onRemove?: () => void; onEdit?: () => void }[] =
      kind === 'installed'
        ? place.installedDevices.map((entry) => ({
            key: `${place.cityId}_${entry._id}`,
            content: toDisplayDevice(entry),
            onRemove:
              place.installedDevices.length > 1
                ? () => DeviceDialogStore.removeInstalledDevice(place, entry)
                : undefined,
            onEdit: () => openEditInstalledDevice(place, entry)
          }))
        : place.devices.map((device) => ({
            key: `${place.cityId}_${device._id}`,
            content: device,
            onRemove: place.devices.length > 1 ? () => DeviceDialogStore.removeDevice(place, device) : undefined
          }));

    return (
      <div key={`${kind}_${place.cityId}_${place.street}`}>
        <Grid container className='pa-2 bgGreyLight' alignItems='center' style={{ borderRadius: 5 }}>
          <Grid item xs={3}>
            <div>
              <span style={{ fontWeight: 500 }}>Obec:</span>&nbsp;
              {place.city}
            </div>
            <div>
              <span style={{ fontWeight: 500 }}>Ulica:</span>&nbsp;
              {place.street}
            </div>
            <div>
              <span style={{ fontWeight: 500 }}>Kat. územie:</span>&nbsp;
              {place.catastral}
            </div>
            <div>
              <span style={{ fontWeight: 500 }}>Číslo parcely:</span>&nbsp;
              {place.lot_number}
            </div>
          </Grid>
          <Grid item container xs justifyContent='flex-end' spacing={2}>
            {!readonly && kind === 'devices' && (
              <Grid item>
                <XsButton
                  fullWidth={false}
                  variant='outlined'
                  color='success'
                  label='request_form.add_device'
                  icon={<i className='fal fa-plus fa-lg' />}
                  onClick={() => DeviceDialogStore.open(place)}
                />
              </Grid>
            )}
            {!readonly && (
              <Grid item>
                <XsButton
                  fullWidth={false}
                  variant='outlined'
                  color='error'
                  label='request_form.remove_place'
                  icon={<i className='fal fa-times fa-lg' />}
                  onClick={() => DeviceDialogStore.removePlace(place)}
                />
              </Grid>
            )}
            {canAddInstalledDevices && kind === 'devices' && (
              <Grid item>
                <XsButton
                  fullWidth={false}
                  variant='outlined'
                  color='success'
                  label='request_form.fill_installed_devices'
                  icon={<i className='fal fa-plus fa-lg' />}
                  onClick={() => {
                    installDevicesPlaceRef.current = place;
                    RequestFormStore.setRequestType('reimbursement');
                    DeviceDialogStore.setRegionTypeId(String(getValues('region_type_id') ?? ''));
                    const rid = getValues('round_id');
                    DeviceDialogStore.setRoundId(
                      String((typeof rid === 'object' && rid ? (rid as IRoundsRow).round_id : rid) ?? '')
                    );
                    const roundRow: IRoundsRow | undefined =
                      typeof rid === 'object' && rid
                        ? (rid as IRoundsRow)
                        : RequestFormStore.roundsData.find((x) => x.round_id === rid);
                    DeviceDialogStore.setDeviceGroupId(roundRow?.devide_group_id ?? '');
                    DeviceDialogStore.open(place);
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        {rows.map((row) => (
          <div key={row.key} className='pt-2 pb-2' style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 300, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: gridTpl, gap: 16, minWidth: gridMinW }}>
                {columns.map((col) => (
                  <div key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                    {col.render(row.content)}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ width: row.onEdit ? 90 : 50, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
              {rowActionsEnabled && row.onEdit && (
                <XsIconButton
                  icon={<i className='fal fa-pen' />}
                  tooltip={String(t('device_dialog.installed_table_edit'))}
                  onClick={row.onEdit}
                />
              )}
              {rowActionsEnabled && row.onRemove && (
                <XsIconButton
                  icon={<i className='fal fa-times fa-lg' />}
                  tooltip={row.onEdit ? String(t('device_dialog.installed_table_delete')) : undefined}
                  onClick={row.onRemove}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Vlastníci žiadateľa — sekcia viditeľná len pre právnu formu družstvo (J.D.) / akciová spoločnosť (a.s.)
  const companyLegalFormVal = watch('companyLegalForm');
  const isAllowOwners = companyLegalFormVal === 'druzstvo' || companyLegalFormVal === 'as';

  const emptyOwner: IOwnerForm = { type: 'legalEntity', name: '', rpoSearch: '', rpoName: '', ownershipInterest: '' };
  const resizeOwners = (count: number) => {
    const current = getValues('owners') ?? [];
    if (count > current.length) {
      for (let i = current.length; i < count; i++) appendOwner({ ...emptyOwner });
    } else if (count < current.length) {
      for (let i = current.length - 1; i >= count; i--) removeOwner(i);
    }
  };

  // ─── Číselník zariadení (pre DeviceDialog + výpočet hodnoty poukážky) ──────
  useServerCall<unknown, ListResponse<IDeviceRow>>({
    key: 'cl_device',
    request: { method: 'POST', url: API.deviceCodelist(), data: getFilters([], { row_count_full: 1000 }) },
    queryOptions: { refetchOnWindowFocus: false, staleTime: Infinity },
    onSuccess: (res) => CodelistStore.setCodelist('device', res.data?.rows ?? [])
  });

  // Číselníky pre selecty žiadateľa (Fáza 2)
  useServerCall<unknown, ListResponse<ICodelistRow>>({
    key: 'cl_legal_form',
    request: { method: 'POST', url: API.legalFormCodelist(), data: getFilters([], { row_count_full: 1000 }) },
    queryOptions: { refetchOnWindowFocus: false, staleTime: Infinity },
    onSuccess: (res) => CodelistStore.setCodelist('legal_form', res.data?.rows ?? [])
  });
  useServerCall<unknown, ListResponse<ICodelistRow>>({
    key: 'cl_company_size',
    request: { method: 'POST', url: API.companySizeCodelist(), data: getFilters([], { row_count_full: 1000 }) },
    queryOptions: { refetchOnWindowFocus: false, staleTime: Infinity },
    onSuccess: (res) => CodelistStore.setCodelist('company_size', res.data?.rows ?? [])
  });
  useServerCall<unknown, ListResponse<ICodelistRow>>({
    key: 'cl_sk_nace',
    request: { method: 'POST', url: API.skNaceCodelist(), data: getFilters([], { row_count_full: 5000 }) },
    queryOptions: { refetchOnWindowFocus: false, staleTime: Infinity },
    onSuccess: (res) => CodelistStore.setCodelist('sk_nace', res.data?.rows ?? [])
  });

  // ─── Kolá výzvy ──────────────────────────────────────────────────────────
  useServerCall<unknown, ListResponse<IRoundsRow>>({
    key: 'gd_rounds',
    request: { method: 'GET', url: API.rounds() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => RequestFormStore.setRoundsData(res.data?.rows ?? [])
  });

  // Po načítaní kôl: ak round_id drží reťazec (z načítanej žiadosti), nahraď ho objektom (pre react-select label)
  useEffect(() => {
    const rid = getValues('round_id');
    if (typeof rid === 'string' && rid) {
      const found = RequestFormStore.roundsData.find((x) => x.round_id === rid);
      if (found) setValue('round_id', found);
    }
  }, [RequestFormStore.roundsData.length]);
  // ─── Načítanie firmy pri novej žiadosti ─────────────────────────────────
  const companyId = getCompanyId();
  const companyCall = useServerCall<unknown, ICompanyFlatData>({
    key: ['company_data', companyId ?? ''],
    request: { method: 'GET', url: companyId ? API.companyData(companyId) : '' },
    disabled: !!id || !companyId, // len pri novej žiadosti
    queryOptions: { refetchOnWindowFocus: false, cacheTime: 0 }
  });

  // Predvyplnenie identifikácie žiadateľa pri novej žiadosti (cez vrátené data — spoľahlivé)
  useEffect(() => {
    const c = companyCall.data;
    if (!c || id) return;
    setValue('ico', c.identifier ?? '');
    setValue('dic', c.tax_identification_number ?? '');
    setValue('icdph', c.vat_identification_number ?? '');
    setValue('companyName', c.name ?? '');
    setValue('companyLegalForm', c.legal_form_id ?? '');
    setValue('companyIBAN', c.iban ?? '');
    setValue('companySWIFT', c.swift ?? '');
    setValue('companySize', c.company_size_id ?? '');
    setValue('skNace', c.classification_id ?? '');
    setValue('companyStreet', c.street_plain ?? '');
    setValue('companyCity', c.city_id ?? '');
    RequestFormStore.setShowSwift(!!c.swift);
  }, [companyCall.data]);

  // ─── Načítanie existujúcej žiadosti (proxy out — dáta na KORENI odpovede) ──
  // Pozn.: populácia cez vrátené `data` + useEffect (spoľahlivé aj pri cache hite, nie len onSuccess).
  const recordCall = useServerCall<unknown, IGdRecordProxyOut>({
    key: ['gd_record', id ?? 'new'],
    request: { method: 'GET', url: id ? API.loadRequest(id) : '' },
    disabled: !id,
    queryOptions: { refetchOnWindowFocus: false, cacheTime: 0 }
  });

  // Nová žiadosť (bez id) — editovateľná, Uložiť/Odoslať viditeľné
  useEffect(() => {
    if (!id) {
      RequestFormStore.setReadonly(false);
      RequestFormStore.setRequestInProgress(true);
      RequestFormStore.setShowEditButton(false);
      // Voľba z RequestTypeDialog (napr. "Zariadenie už mám nainštalované") — prenesená do formulára
      RequestFormStore.setRequestType(RequestTypeDialogStore.requestType);
    }
  }, [id]);

  // Naplnenie formulára z načítanej žiadosti
  useEffect(() => {
    const r = recordCall.data;
    if (!r) return;
    const company = r.company_data ?? {};
    const addr = company.oa_address ?? {};
    const contact = r.request_contact_data ?? {};
    const empowered = r.empowered_contact_data ?? {};
    const cityVal = typeof addr.obj_city === 'object' ? addr.obj_city?._id : (addr.obj_city as string | undefined);

    const owners: IOwnerForm[] = (r.owners_data ?? []).map((o) =>
      o.company_identifier
        ? {
            type: 'legalEntity',
            name: '',
            rpoSearch: o.company_identifier,
            rpoName: o.company_name ?? '',
            ownershipInterest: o.ownership_share ?? ''
          }
        : {
            type: 'naturalPerson',
            name: o.person_name ?? '',
            rpoSearch: '',
            rpoName: '',
            ownershipInterest: o.ownership_share ?? ''
          }
    );

    reset({
      ...getValues(),
      ico: company.identifier ?? '',
      dic: company.tax_identification_number ?? '',
      icdph: company.vat_identification_number ?? '',
      companyName: company.name ?? company.company ?? '',
      companyLegalForm: company.legal_form_id ?? '',
      companyIBAN: company.iban ?? '',
      companySWIFT: company.swift ?? '',
      companySize: company.company_size_id ?? '',
      skNace: company.classification_id ?? '',
      companyStreet: addr.street_plain ?? company.street_plain ?? '',
      companyCity: cityVal ?? company.city_id ?? '',
      contactPersonName: contact.name ?? '',
      contactPersonPhone: contact.phone ?? '',
      contactPersonMail: contact.email ?? '',
      authorizedPersonName: empowered.name ?? '',
      authorizedPersonPhone: empowered.phone ?? '',
      authorizedPersonMail: empowered.email ?? '',
      round_id:
        RequestFormStore.roundsData.find((x) => x.round_id === r.round_data?.round_id) ?? r.round_data?.round_id ?? '',
      region_type_id: r.round_data?.region_type_id ?? '',
      contributionForEnergyAudit: !!r.request_for_ea_grant,
      communicationMethod: company.communication_method ? 'electronicCommunication' : 'writtenCommunication',
      numberOfOwners: owners.length,
      owners,
      // Krok 3 (Vyhlásenie k minimálnej pomoci) a krok 4 (Čestné vyhlásenie) — dvojice
      // No/Yes odvodené z jedného boolean poľa z BE; undefined necháva oba checkboxy nezaškrtnuté.
      singleEnterpriseYes: r.declaration_one_company === true,
      singleEnterpriseNo: r.declaration_one_company === false,
      illegalAidReturn: !!r.declaration_one_company_no_refund,
      excludedSectorsYes: r.declaration_operate_in_sector === true,
      excludedSectorsNo: r.declaration_operate_in_sector === false,
      otherMinAidYes: r.declaration_other_min_request === true,
      otherMinAidNo: r.declaration_other_min_request === false,
      consentConditions: !!r.affidativ
    });
    RequestFormStore.setShowSwift(!!company.swift);
    setGdRequestId(r.gd_request_id ?? '');
    setCompanyCityName(addr.city ?? '');

    // round_text — poskladaný z round_data (response nemá samostatné pole), verný port load()
    const rd = r.round_data;
    setRoundText(
      rd
        ? `${rd.region_type_code_ext ?? ''} + ${rd.devide_group_name_ext ?? ''} ${fmtDate(
            rd.validity_from
          )} - ${fmtDate(rd.validity_to)}`
        : ''
    );
    setLoadedVoucher(r.expected_voucher_amount ?? 0);

    setStatutories(Array.isArray(r.statutories_data) && r.statutories_data.length ? r.statutories_data : ['']);

    // 3.1 Podniky tvoriace jediný podnik so žiadateľom (lokálny stav, mimo react-hook-form)
    const entities = r.declaration_one_company_entities_data ?? [];
    setRpoSearchCompanies(
      entities.length
        ? entities.map((e, i) => ({
            id: i,
            value: e.company_identifier ? { identifier: e.company_identifier, name: e.company_name ?? '' } : null
          }))
        : [{ id: 0, value: null }]
    );
    rpoSearchNextId.current = entities.length || 1;

    // 3.4 Iná minimálna pomoc — dynamický zoznam poskytovateľov (lokálny stav)
    const otherAid = r.declaration_other_min_requests_data ?? [];
    setOtherMinAidEntries(
      otherAid.length
        ? otherAid.map((e, i) => ({
            id: i,
            provider: e.provider_name ?? '',
            amount: e.amount != null ? String(e.amount) : '',
            date: e.application_date ? new Date(e.application_date) : null
          }))
        : [{ id: 0, provider: '', amount: '', date: null }]
    );
    otherMinAidNextId.current = otherAid.length || 1;

    // Miesta realizácie → DeviceDialogStore (response nesie city_name_ext aj device_name_ext/device_code)
    if (Array.isArray(r.project_implementations_data)) {
      const places: IPlaceEntry[] = r.project_implementations_data.map((impl) => ({
        cityId: impl.city_id,
        // čistý názov obce: city_name_ext, inak city_with_county bez okresu v zátvorke
        city: impl.city_name_ext ?? impl.city_with_county?.replace(/\s*\(.*$/, '') ?? impl.city_id,
        street: impl.street_plain ?? '',
        catastral: impl.catastral || undefined,
        lot_number: impl.lot_number || undefined,
        projectImplementationId: impl.project_implementation_id || undefined,
        devices: (impl.devices_data ?? []).map((dev, i) => ({
          _id: `${impl.city_id}_${dev.device_id}_${i}`,
          deviceId: dev.device_id,
          deviceCode: dev.device_code ?? '',
          device: dev.device_name_ext ?? '',
          power: Number(dev.power) || 0,
          capacity:
            dev.accumulation_capacity != null && dev.accumulation_capacity !== ''
              ? Number(dev.accumulation_capacity)
              : undefined,
          deviceTypeName: dev.device_type_name || undefined,
          manufacturer: dev.manufacturer || undefined,
          modelType: dev.model_type || undefined,
          count: dev.count != null && dev.count !== '' ? Number(dev.count) : undefined
        })),
        // installed_devices_data (reimbursement) sa pri načítaní žiadosti zatiaľ nemapuje späť
        installedDevices: []
      }));
      DeviceDialogStore.setPlaces(places);
    }

    // Prílohy → RequestFormStore.attachments (XsDropzone disabled ich zobrazí + stiahne cez _id)
    const attMap: Record<string, IAttachment[]> = {};
    (r.attached_files ?? []).forEach((att) => {
      const key = att.type as AttachmentKey | InstalledAttachmentKey;
      if (!ALL_ATTACHMENT_KEYS.includes(key)) return;
      if (!attMap[key]) attMap[key] = [];
      attMap[key].push({
        _id: att.attachment_id,
        file_name: att.file_name ?? 'súbor',
        content_type: att.content_type ?? 'application/octet-stream',
        content: ''
      });
    });
    ALL_ATTACHMENT_KEYS.forEach((k) => RequestFormStore.setAttachments(k, attMap[k] ?? []));

    // Stavy: načítaná žiadosť je vždy readonly; editovať možno len 'inp' (cez Upraviť)
    RequestFormStore.setReadonly(true);
    RequestFormStore.setRequestInProgress(r.status_id === 'inp');
    RequestFormStore.setShowEditButton(r.status_id === 'inp');
    setLoadedStatusId(r.status_id ?? '');
  }, [recordCall.data]);

  // Po zatvorení DeviceDialogu otvoreného cez "Doplniť inštalované zariadenia" (conf-reserv tok):
  // vráti globálny requestType späť na 'reservation' a ak boli pre dané miesto pridané nejaké
  // installedDevices, nahradí nimi pôvodné (rezervované) devices tohto miesta.
  useEffect(() => {
    if (DeviceDialogStore.isOpen) return;
    const targetPlace = installDevicesPlaceRef.current;
    if (!targetPlace) return;
    installDevicesPlaceRef.current = null;
    RequestFormStore.setRequestType('reservation');
    const place = DeviceDialogStore.places.find(
      (p) => p.cityId === targetPlace.cityId && p.street === (targetPlace.street ?? '').trim()
    );
    if (place && place.installedDevices.length > 0 && place.devices.length > 0) {
      DeviceDialogStore.replaceDevicesWithInstalled(place);
    }
  }, [DeviceDialogStore.isOpen]);

  useEffect(() => {
    return () => {
      RequestFormStore.reset();
      DeviceDialogStore.reset();
      RequestFormStepperStore.reset();
    };
  }, []);

  // ─── Uloženie / odoslanie ───────────────────────────────────────────────
  const saveCall = useServerCall<IGdRecordSave, IGdRecordSaveResponse>({
    key: 'save_request',
    mode: 'control',
    request: { method: 'PUT', url: API.saveRequest() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      GlobalStore.loading(false);
      ConsentDialogStore.close();
      if (res.data?._id) savedRecordIdRef.current = res.data._id;
      if (res.data?.gd_request_id) setGdRequestId(res.data.gd_request_id);
      const warning = res.data?.warning_message ?? res.data?.WarningMessage;
      if (warning) WarningDialogStore.open(String(warning));
      if (continueAfterSaveRef.current) {
        continueAfterSaveRef.current = false;
        RequestFormStepperStore.next();
      } else if (pendingStatus === 'sent' || pendingStatus === 'valid') {
        history.push('/requests');
      }
    },
    onError: () => GlobalStore.loading(false)
  });

  const buildPayload = (statusId: string): IGdRecordSave => {
    const v = getValues();

    // device_is_installed rozhoduje, či sa za miesto posiela devices_data (rezervácia)
    // alebo installed_devices_data (zariadenie už nainštalované — reimbursement).
    // Pri žiadosti bez inštalácie (conf-reserv) môže byť konkrétne miesto doplnené o
    // inštalované zariadenia (viď "Doplniť inštalované zariadenia") — vtedy p.devices je
    // prázdne a za dané miesto sa tiež posiela installed_devices_data; akonáhle má aspoň
    // jedno miesto doplnené inštalované zariadenia, prepne sa aj celkový device_is_installed na true.
    const hasAnyInstalledDevice = DeviceDialogStore.places.some((p) => p.installedDevices.length > 0);
    const deviceIsInstalled = RequestFormStore.requestType === 'reimbursement' || hasAnyInstalledDevice;

    const projectImplementations: IProjectImplementation[] = DeviceDialogStore.places.map((p) => ({
      // Len pri mieste načítanom z existujúcej žiadosti (viď mapovanie vyššie) — BE ho vtedy
      // aktualizuje namiesto vytvorenia nového; nové miesto pridané vo formulári ho nemá.
      project_implementation_id: p.projectImplementationId,
      city_id: p.cityId,
      street_plain: p.street,
      cadastral_name: p.catastral,
      lot_number: p.lot_number,
      ...(deviceIsInstalled || (p.devices.length === 0 && p.installedDevices.length > 0)
        ? {
            installed_devices_data: p.installedDevices.map((d) => ({
              energetic_device_id: d.energeticDeviceId,
              count: d.count,
              // TODO: InstalledDeviceSelectDialog zatiaľ zbiera prílohy do jedného zoznamu
              // (bez rozlíšenia PRODUCTIONLABELPHOTO / DEVINOPERATIONPHOTO) — dočasne jeden typ
              attached_files: (d.attachments ?? []).map((att) =>
                att._id
                  ? {
                      attachment_id: att._id,
                      type: 'GD:GDREQ:PRJIMP:INSTDEV:PRODUCTIONLABELPHOTO',
                      content_type: att.content_type,
                      file_name: att.file_name
                    }
                  : {
                      type: 'GD:GDREQ:PRJIMP:INSTDEV:PRODUCTIONLABELPHOTO',
                      content_type: att.content_type,
                      content: att.content,
                      file_name: att.file_name
                    }
              )
            }))
          }
        : {
            devices_data: p.devices.map((d) => ({
              device_id: d.deviceId,
              power: d.power,
              accumulation_capacity: d.capacity,
              cadastral_name: d.catastral,
              lot_number: d.lot_number,
              device_type_name: d.deviceTypeName,
              manufacturer: d.manufacturer,
              model_type: d.modelType,
              count: d.count
            }))
          })
    }));

    const attachedFiles = ALL_ATTACHMENT_KEYS.flatMap((key) =>
      (RequestFormStore.attachments[key] ?? []).map((att: IAttachment) =>
        att._id
          ? {
              attachment_id: att._id,
              type: key,
              content_type: att.content_type,
              file_name: att.file_name
            }
          : {
              type: key,
              content_type: att.content_type,
              content: att.content,
              file_name: att.file_name
            }
      )
    );

    const roundId =
      typeof v.round_id === 'object' && v.round_id ? (v.round_id as IRoundsRow).round_id : (v.round_id as string);

    const ownersData: IOwnerData[] | undefined =
      isAllowOwners && v.owners && v.owners.length > 0
        ? v.owners.map((o) =>
            o.type === 'naturalPerson'
              ? { company_name: '', company_identifier: '', person_name: o.name, ownership_share: o.ownershipInterest }
              : {
                  company_name: o.rpoName,
                  company_identifier: o.rpoSearch,
                  person_name: '',
                  ownership_share: o.ownershipInterest
                }
          )
        : undefined;

    const declaration_one_company = v.singleEnterpriseYes ? true : false;
    const declaration_one_company_entities_data = rpoSearchCompanies
      .map((entry) => entry.value)
      .filter((value): value is { identifier: string; name: string } => Boolean(value))
      .map((company) => ({
        company_name: company.name,
        company_identifier: company.identifier
      }));
    const declaration_other_min_requests_data = otherMinAidEntries
      .map((entry) => ({
        provider_name: entry.provider,
        amount: entry.amount ? Number(entry.amount) : undefined,
        application_date: entry.date ? entry.date.toISOString().slice(0, 10) : undefined
      }))
      .filter((entry) => entry.provider_name || entry.amount != null || entry.application_date);

    return {
      _type: 'GD.API.JSON.Proxy.Request.GDRequest',
      record_id: savedRecordIdRef.current ?? null,
      type_id: 'GD.Data.Request.GDRequest',
      status_id: statusId,
      round_data: roundId ? { round_id: roundId } : null,
      device_is_installed: deviceIsInstalled,
      company_data: {
        company: companyId,
        tax_identification_number: v.dic,
        vat_identification_number: v.icdph,
        legal_form_id: v.companyLegalForm,
        iban: v.companyIBAN,
        swift: v.companySWIFT || null,
        company_size_id: v.companySize,
        classification_id: v.skNace,
        is_vat_registered: v.vatPayer || false,
        oa_address: {
          obj_city: { _type: 'Entity.CL.Address.City', _id: v.companyCity },
          primary: true,
          street_plain: v.companyStreet.trim(),
          type: { _type: 'Entity.CL.AddressType', _id: 'OA' }
        }
      },
      project_implementations_data: projectImplementations,
      statutories_data: statutories.filter((s) => s.trim() !== ''),
      owners_data: ownersData,
      request_contact_data: {
        name: v.contactPersonName,
        email: v.contactPersonMail,
        phone: v.contactPersonPhone.replace(/\s/g, '')
      },
      empowered_contact_data:
        v.authorizedPersonName || v.authorizedPersonMail || v.authorizedPersonPhone
          ? {
              name: v.authorizedPersonName,
              email: v.authorizedPersonMail,
              phone: (v.authorizedPersonPhone ?? '').replace(/\s/g, '')
            }
          : null,
      request_for_ea_grant: !!v.contributionForEnergyAudit,
      expected_voucher_amount: DeviceDialogStore.assumedVoucherValue + (v.contributionForEnergyAudit ? 2500 : 0),
      // Súhlas potvrdený cez ConsentDialog
      consent_personal_data_processing: true,
      attached_files: attachedFiles,
      declaration_one_company,
      declaration_one_company_entities_data,
      declaration_one_company_no_refund: v.illegalAidReturn ? true : false,
      declaration_operate_in_sector: v.excludedSectorsYes ? true : false,
      declaration_other_min_request: v.otherMinAidYes ? true : false,
      declaration_other_min_requests_data,
      affidativ: v.consentConditions ? true : false
    };
  };

  // Doplnková validácia (verný port hooks.onSubmit) — miesta + povinné prílohy
  const validateExtra = (): boolean => {
    if (DeviceDialogStore.places.length === 0) {
      GlobalStore.setNotificationMessage(t('request_form.err_no_place'), '', 'error');
      return false;
    }
    const att = RequestFormStore.attachments;
    const required =
      att['GD:GDREQ:ENERGETICAUDIT'].length < 1 ||
      att['GD:GDREQ:OZE'].length < 1 ||
      att['GD:GDREQ:MINIMALDECLARATION'].length < 1 ||
      att['GD:GDREQ:AFFIDAVIT'].length < 1 ||
      (att['GD:GDREQ:POWEROFATTORNEY'].length < 1 && !!getValues('authorizedPersonName'));
    if (required) {
      GlobalStore.setNotificationMessage(t('request_form.err_no_attachment'), '', 'error');
      return false;
    }
    if (statutories.filter((s) => s.trim() !== '').length === 0) {
      GlobalStore.setNotificationMessage(t('request_form.err_no_statutory'), '', 'error');
      return false;
    }
    return true;
  };

  const validateStep2Selections = (): boolean => {
    const singleSelected = !!getValues('singleEnterpriseNo') || !!getValues('singleEnterpriseYes');
    const excludedSelected = !!getValues('excludedSectorsNo') || !!getValues('excludedSectorsYes');
    const otherAidSelected = !!getValues('otherMinAidNo') || !!getValues('otherMinAidYes');

    const missing: string[] = [];
    if (!singleSelected) missing.push('Jediný podnik');
    if (!excludedSelected) missing.push('Vylúčené sektory');
    if (!otherAidSelected) missing.push('Iná minimálna pomoc');

    if (missing.length > 0) {
      GlobalStore.setNotificationMessage(`Vyberte jednu možnosť pre: ${missing.join(', ')}`, '', 'error');
      return false;
    }

    return true;
  };

  const openConsent = (status: 'inp' | 'sent') => {
    // Finálne odoslanie (status 'sent') validuje celý formulár + doplnkové podmienky.
    if (status === 'sent') {
      // Žiadosť bez inštalácie v stave "potvrdená rezervácia" (conf-reserv), doplnená o
      // inštalované zariadenia — finálne odoslanie ju posunie do stavu "platná" (valid),
      // nie späť do 'sent' (to je stav pre prvé odoslanie novej žiadosti).
      const targetStatus: 'sent' | 'valid' = loadedStatusId === 'conf-reserv' ? 'valid' : 'sent';
      return handleSubmit(() => {
        if (currentStep !== STEP_COUNT - 1) return;
        if (!validateStep2Selections()) return;
        if (!validateExtra()) return;
        setPendingStatus(targetStatus);
        ConsentDialogStore.open();
      });
    }
    // Uloženie rozpracovanej žiadosti (draft) a pokračovanie bez modalu.
    return async () => {
      const fields = STEP_FIELD_NAMES[currentStep] ?? [];
      const valid = fields.length === 0 || (await trigger(fields));
      if (!valid) return;
      setPendingStatus(status);
      saveCall.run({ data: buildPayload(status) });
    };
  };

  const onConfirmed = () => {
    GlobalStore.loading(true);
    saveCall.run({ data: buildPayload(pendingStatus) });
  };

  const legalFormOptions = CodelistStore.getCodelist<ICodelistRow>('legal_form');
  const companySizeOptions = CodelistStore.getCodelist<ICodelistRow>('company_size');
  const skNaceOptions = CodelistStore.getCodelist<ICodelistRow>('sk_nace');

  const clLabel = (o: ICodelistRow) => o?.name_ext ?? '';
  const clValue = (o: ICodelistRow) => o?._id ?? '';
  // Názov položky číselníka podľa id (pre readonly zobrazenie selectov ako text)
  const clName = (key: string, idVal?: string) =>
    CodelistStore.getCodelist<ICodelistRow>(key).find((o) => o._id === idVal)?.name_ext ?? idVal ?? '';

  const voucherAmount =
    readonly && loadedVoucher != null
      ? loadedVoucher
      : DeviceDialogStore.assumedVoucherValue + (contributionForEnergyAudit ? 2500 : 0);

  const summaryDevices = DeviceDialogStore.places.flatMap((place) =>
    place.devices.map((device) => device.device).filter(Boolean)
  );
  const summaryDeviceLabel = summaryDevices.length > 0 ? summaryDevices.join(', ') : '-';

  const summaryPowerValues = DeviceDialogStore.places.flatMap((place) =>
    place.devices.map((device) => `${device.power} kW`).filter(Boolean)
  );
  const summaryPowerLabel = summaryPowerValues.length > 0 ? summaryPowerValues.join(', ') : '-';

  const summaryLocations = DeviceDialogStore.places
    .map((place) => [place.city, place.street].filter(Boolean).join(', '))
    .filter(Boolean);
  const summaryLocationLabel = summaryLocations.length > 0 ? summaryLocations.join(' / ') : '-';

  const summaryApplicantName = watch('companyName') || '-';
  const summaryApplicantIco = watch('ico') || '-';
  const summaryEnergyAuditLabel = contributionForEnergyAudit ? t('global.yes') : t('global.no');

  return (
    <>
      <BaseHeader />
      <XsHeader />
      <Container maxWidth='lg' className='request_form_fields'>
        <Box pt={2}>
          <RequestFormStepper />
        </Box>
        <Grid container spacing={2} className='pt-4'>
          <Grid item xs={12}>
            <Typography variant='h4'>
              {t('request_form.title')} {gdRequestId}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='body1' className='mb-3 bold'>
              {t('request_form.subtitle')}
            </Typography>
          </Grid>
        </Grid>

        <FormProvider {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            {currentStep === 0 && (
              <>
                {/* ───────── 1. Údaje o žiadateľovi ───────── */}
                <Grid container spacing={2} className='pt-2'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>{t('request_form.section_applicant')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2'>{t('request_form.required_note')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body1' className='bold'>
                      {t('request_form.applicant_identification')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6} lg={4}>
                    <Controller
                      name='companyName'
                      control={control}
                      render={({ field }) => (
                        <XsTextField {...field} required disabled label={t('request_form.company_name_label')} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Controller
                      name='ico'
                      control={control}
                      render={({ field }) => (
                        <XsNumberField {...field} required disabled label={t('request_form.ico')} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    {readonly ? (
                      <XsTextField
                        value={clName('legal_form', watch('companyLegalForm'))}
                        name='companyLegalForm_ro'
                        disabled
                        label={t('request_form.legal_form')}
                      />
                    ) : (
                      <Controller
                        name='companyLegalForm'
                        control={control}
                        render={({ field }) => (
                          <XsSearchSelect
                            {...field}
                            value={legalFormOptions.find((o) => o._id === field.value) ?? null}
                            onChange={(o) => field.onChange((o as ICodelistRow | null)?._id ?? '')}
                            name='companyLegalForm'
                            label={t('request_form.legal_form')}
                            required
                            disabled={readonly}
                            options={legalFormOptions}
                            getOptionLabel={clLabel}
                            getOptionValue={clValue}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {readonly ? (
                      <XsTextField
                        value={companyCityName || String(getValues('companyCity') ?? '')}
                        name='companyCity_ro'
                        disabled
                        label={t('request_form.city')}
                      />
                    ) : (
                      <Controller
                        name='companyCity'
                        control={control}
                        render={({ field }) => (
                          <XsAutocompleteServer
                            {...field}
                            label={t('request_form.city')}
                            required
                            disabled={readonly}
                            minLengthForSearch={1}
                            request={{
                              uri: API.cityCodelist(),
                              payload: {
                                load: (v: string) => getFilters([`code=${v}`]),
                                search: (s: string) => getFilters([`search_column=${s}`])
                              }
                            }}
                            identFn={(o: ICityRow) => o._id}
                            labelFn={(o: ICityRow) => (o ? ((o.city_county as string) ?? o.name_ext ?? '') : '')}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name='companyStreet'
                      control={control}
                      render={({ field }) => (
                        <XsTextField {...field} required disabled={readonly} label={t('request_form.street')} />
                      )}
                    />
                  </Grid>

                  <Grid item container spacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                      <Controller
                        name='companyIBAN'
                        control={control}
                        render={({ field }) => (
                          <XsTextField
                            {...field}
                            required
                            disabled={readonly}
                            label={t('request_form.iban')}
                            onBlur={(e) => {
                              field.onBlur();
                              const val = (e.target.value ?? '').toString();
                              if (isIBANValid(val)) {
                                RequestFormStore.setShowSwift(val.substring(0, 2) !== 'SK');
                              } else {
                                setValue('companySWIFT', '');
                                RequestFormStore.setShowSwift(false);
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    {RequestFormStore.showSwift && (
                      <Grid item xs={12} md={6} lg={4}>
                        <Controller
                          name='companySWIFT'
                          control={control}
                          render={({ field }) => (
                            <XsTextField {...field} disabled={readonly} label={t('request_form.swift')} />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6} lg={2}>
                      <Controller
                        name='dic'
                        control={control}
                        render={({ field }) => (
                          <XsTextField
                            {...field}
                            required
                            disabled={readonly}
                            label={t('request_form.dic')}
                            maxLength={10}
                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={2}>
                      <Controller
                        name='icdph'
                        control={control}
                        render={({ field }) => (
                          <XsTextField
                            {...field}
                            required
                            disabled={readonly}
                            label={t('request_form.icdph')}
                            tooltip={t('request_form.icdph_tooltip')}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={2} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Controller
                        name='vatPayer'
                        control={control}
                        render={({ field }) => (
                          <XsCheckbox controllerProps={field} disabled={readonly} label={t('request_form.vat_payer')} />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={6} lg={3}>
                    {readonly ? (
                      <XsTextField
                        value={clName('company_size', watch('companySize'))}
                        name='companySize_ro'
                        disabled
                        label={t('request_form.company_size')}
                      />
                    ) : (
                      <Controller
                        name='companySize'
                        control={control}
                        render={({ field }) => (
                          <XsSearchSelect
                            {...field}
                            value={companySizeOptions.find((o) => o._id === field.value) ?? null}
                            onChange={(o) => field.onChange((o as ICodelistRow | null)?._id ?? '')}
                            name='companySize'
                            label={t('request_form.company_size')}
                            required
                            disabled={readonly}
                            options={companySizeOptions}
                            getOptionLabel={clLabel}
                            getOptionValue={clValue}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    {readonly ? (
                      <XsTextField
                        value={clName('sk_nace', watch('skNace'))}
                        name='skNace_ro'
                        disabled
                        label={t('request_form.sk_nace')}
                      />
                    ) : (
                      <Controller
                        name='skNace'
                        control={control}
                        render={({ field }) => (
                          <XsSearchSelect
                            {...field}
                            value={skNaceOptions.find((o) => o._id === field.value) ?? null}
                            onChange={(o) => field.onChange((o as ICodelistRow | null)?._id ?? '')}
                            name='skNace'
                            label={t('request_form.sk_nace')}
                            required
                            disabled={readonly}
                            options={skNaceOptions}
                            getOptionLabel={clLabel}
                            getOptionValue={clValue}
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>

                {/* ── Vlastníci žiadateľa (len pre právnu formu družstvo / a.s.) ── */}
                {isAllowOwners && (
                  <Grid container spacing={2} className='mt-4'>
                    <Grid item xs={12}>
                      <Typography variant='body1' className='bold'>
                        {companyLegalFormVal === 'as' && t('request_form.shareholders_section')}
                        {companyLegalFormVal === 'druzstvo' && t('request_form.cooperatives_section')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                      <Controller
                        name='numberOfOwners'
                        control={control}
                        render={({ field }) => (
                          <XsNumberField
                            {...field}
                            disabled={readonly}
                            label={t('request_form.owners_count')}
                            inputProps={{ min: 0, max: 20 }}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') {
                                field.onChange('');
                                return;
                              }
                              const val = Math.max(0, Math.min(20, Number(raw) || 0));
                              field.onChange(val);
                              resizeOwners(val);
                            }}
                            onBlur={() => {
                              field.onBlur();
                              if (field.value?.toString() === '') {
                                field.onChange(0);
                                resizeOwners(0);
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container direction='column' spacing={1}>
                        {ownerFields.map((row, index) => (
                          <Grid item container spacing={2} alignItems='center' key={row.id}>
                            <Grid item xs={12} md={5}>
                              <Controller
                                name={`owners.${index}.type` as OwnerFieldName}
                                control={control}
                                render={({ field }) => (
                                  <RadioGroup row value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                                    <FormControlLabel
                                      value='legalEntity'
                                      control={<Radio disabled={readonly} />}
                                      label={t('request_form.owner_legal_entity')}
                                      disabled={readonly}
                                    />
                                    <FormControlLabel
                                      value='naturalPerson'
                                      control={<Radio disabled={readonly} />}
                                      label={t('request_form.owner_natural_person')}
                                      disabled={readonly}
                                    />
                                  </RadioGroup>
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              {watch(`owners.${index}.type` as OwnerFieldName) === 'naturalPerson' ? (
                                <Controller
                                  name={`owners.${index}.name` as OwnerFieldName}
                                  control={control}
                                  render={({ field }) => (
                                    <XsTextField
                                      {...field}
                                      required
                                      disabled={readonly}
                                      label={t('request_form.owner_name')}
                                    />
                                  )}
                                />
                              ) : (
                                <Controller
                                  name={`owners.${index}.rpoSearch` as OwnerFieldName}
                                  control={control}
                                  render={({ field }) => (
                                    <XsAutocompleteServerWithoutLoad
                                      {...field}
                                      // field.value je len identifikátor (string) — komponent pri preloade
                                      // potrebuje celý objekt {identifier, name} pre labelFn/tooltip
                                      value={
                                        field.value
                                          ? {
                                              identifier: field.value,
                                              name: watch(`owners.${index}.rpoName` as OwnerFieldName)
                                            }
                                          : null
                                      }
                                      required
                                      label={t('request_form.owner_rpo_search')}
                                      disabled={readonly}
                                      minLengthForSearch={3}
                                      request={{
                                        uri: RPO_SEARCH_URL,
                                        includeToken: false,
                                        payload: {
                                          search: (v: string) => getFilters([`search=${v}`], { row_count_full: 200 })
                                        }
                                      }}
                                      identFn={(o: any) => o.identifier}
                                      labelFn={(o: any) => (o ? `${o.name} ${o.identifier}` : '')}
                                      onApply={(o: any) => setValue(`owners.${index}.rpoName` as any, o?.name ?? '')}
                                    />
                                  )}
                                />
                              )}
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Controller
                                name={`owners.${index}.ownershipInterest` as OwnerFieldName}
                                control={control}
                                render={({ field }) => (
                                  <XsNumberField
                                    {...field}
                                    required
                                    disabled={readonly}
                                    label={t('request_form.owner_share')}
                                    inputProps={{ min: 5, max: 100 }}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* ── Spôsob komunikácie ── */}
                <Grid container spacing={2} className='mt-4'>
                  <Grid item xs={12}>
                    <Typography variant='body1' className='bold'>
                      {t('request_form.communication_method')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className='mb-2'>
                    <Controller
                      name='communicationMethod'
                      control={control}
                      render={({ field }) => (
                        <RadioGroup row value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                          <FormControlLabel
                            value='writtenCommunication'
                            control={<Radio disabled={readonly} />}
                            label={t('request_form.communication_method_written')}
                            disabled={readonly}
                          />
                          <FormControlLabel
                            value='electronicCommunication'
                            control={<Radio disabled={readonly} />}
                            label={t('request_form.communication_method_electronic')}
                            disabled={readonly}
                          />
                        </RadioGroup>
                      )}
                    />
                  </Grid>

                  {/* ── Štatutárny orgán ── */}
                  <Grid item xs={12}>
                    <Typography variant='body1' className='bold mt-1'>
                      {t('request_form.statutory_section')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2'>{t('request_form.statutory_note')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container direction='column' spacing={1}>
                      {statutories.map((s, idx) => (
                        <Grid item container spacing={1} alignItems='center' key={idx}>
                          <Grid item xs={12} md={6} lg={4}>
                            <XsTextField
                              value={s}
                              name={`statutory_${idx}`}
                              disabled={readonly}
                              required
                              label={t('request_form.statutory_name')}
                              onChange={(e) =>
                                setStatutories((prev) => prev.map((x, i) => (i === idx ? e.target.value : x)))
                              }
                            />
                          </Grid>
                          {/* kôš sa skryje, keď je len jeden (povinný) záznam */}
                          {!readonly && statutories.length > 1 && (
                            <Grid item>
                              <XsIconButton
                                icon={<DeleteIcon />}
                                onClick={() =>
                                  setStatutories((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))
                                }
                              />
                            </Grid>
                          )}
                        </Grid>
                      ))}
                      {!readonly && statutories.length < 5 && (
                        <Grid item>
                          <XsButton
                            fullWidth={false}
                            variant='outlined'
                            label='request_form.statutory_add'
                            icon={<i className='fal fa-plus' />}
                            onClick={() => setStatutories((prev) => [...prev, ''])}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* ── Kontaktná osoba ── */}
                  <Grid item xs={12}>
                    <Typography variant='body1' className='bold'>
                      {t('request_form.contact_section')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2' className='mb-2'>
                      {t('request_form.contact_note')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Controller
                      name='contactPersonName'
                      control={control}
                      render={({ field }) => (
                        <XsTextField {...field} required disabled={readonly} label={t('request_form.statutory_name')} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Controller
                      name='contactPersonPhone'
                      control={control}
                      render={({ field }) => (
                        <XsTextField
                          {...field}
                          required
                          disabled={readonly}
                          label={t('request_form.contact_phone')}
                          tooltip={t('request_form.phone_tooltip')}
                          placeholder='+421900111222'
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Controller
                      name='contactPersonMail'
                      control={control}
                      render={({ field }) => (
                        <XsTextField {...field} required disabled={readonly} label={t('request_form.contact_mail')} />
                      )}
                    />
                  </Grid>

                  {/* ── Splnomocnená osoba (ak relevantné) ── */}
                  <Grid item xs={12}>
                    <Typography variant='body1' className='bold'>
                      {t('request_form.authorized_section')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Controller
                      name='authorizedPersonName'
                      control={control}
                      render={({ field }) => (
                        <XsTextField
                          {...field}
                          disabled={readonly}
                          label={t('request_form.statutory_name')}
                          onBlur={(e) => {
                            field.onBlur();
                            if (
                              (e.target.value ?? '').toString().trim() !== '' &&
                              RequestFormStore.attachments['GD:GDREQ:POWEROFATTORNEY'].length < 1
                            ) {
                              GlobalStore.setNotificationMessage(t('request_form.warn_poa'), '', 'warning');
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Controller
                      name='authorizedPersonPhone'
                      control={control}
                      render={({ field }) => (
                        <XsTextField
                          {...field}
                          disabled={readonly}
                          label={t('request_form.contact_phone')}
                          tooltip={t('request_form.phone_tooltip')}
                          placeholder='+421900111222'
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={3}>
                    <Controller
                      name='authorizedPersonMail'
                      control={control}
                      render={({ field }) => (
                        <XsTextField {...field} disabled={readonly} label={t('request_form.contact_mail')} />
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {currentStep === 1 && (
              <>
                {/* ───────── 2. Údaje o projekte ───────── */}
                <Grid container spacing={2} className='mt-4'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>{t('request_form.section_project')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2'>{t('request_form.required_note')}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6} lg={4}>
                    {readonly ? (
                      <XsTextField value={roundText} name='round_text' disabled label={t('request_form.round')} />
                    ) : (
                      <Controller
                        name='round_id'
                        control={control}
                        render={({ field }) => (
                          <XsSearchSelect
                            {...field}
                            name='round_id'
                            label={t('request_form.round')}
                            required
                            disabled={readonly || DeviceDialogStore.places.length > 0}
                            options={RequestFormStore.roundsData}
                            getOptionLabel={roundLabel}
                            getOptionValue={(o: IRoundsRow) => o.round_id}
                            postAction={(o: IRoundsRow | null) => setValue('region_type_id', o?.region_type_id ?? '')}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  {DeviceDialogStore.places.length > 0 && (
                    <>
                      <Grid item xs={12} className='redDanger'>
                        {t('request_form.round_locked_warning')}
                      </Grid>
                      <Grid item xs={12} className='pt-0'>
                        {t('request_form.round_locked_hint')}
                      </Grid>
                    </>
                  )}

                  <Grid item container className='mb-2 mt-2' justifyContent='space-between' alignItems='center'>
                    <Grid item>
                      <Typography variant='body1' className='bold'>
                        {t('request_form.place_section')}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <XsButton
                        fullWidth={false}
                        label='request_form.add_place'
                        icon={<i className='fal fa-plus' />}
                        disabled={readonly || !hasRound}
                        onClick={() => {
                          DeviceDialogStore.setRegionTypeId(String(getValues('region_type_id') ?? ''));
                          const rid = getValues('round_id');
                          DeviceDialogStore.setRoundId(
                            String((typeof rid === 'object' && rid ? (rid as IRoundsRow).round_id : rid) ?? '')
                          );
                          const roundRow: IRoundsRow | undefined =
                            typeof rid === 'object' && rid
                              ? (rid as IRoundsRow)
                              : RequestFormStore.roundsData.find((x) => x.round_id === rid);
                          DeviceDialogStore.setDeviceGroupId(roundRow?.devide_group_id ?? '');
                          DeviceDialogStore.open();
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Tabuľka rezervovaných zariadení — zobrazená len keď je čo zobraziť (rezervačný tok);
                  pri "Zariadenie už mám nainštalované" (reimbursement) ju nahrádza tabuľka nižšie. */}
                  {!showInstalledDeviceColumns && placesWithDevices.length > 0 && (
                    <Grid item xs={12} style={{ border: '3px solid lightgrey', borderRadius: 5 }}>
                      <div className='bold pt-1 pb-1' style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 300, flexShrink: 0 }}>Miesto realizácie projektu</div>
                        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: baseGridTemplate,
                              gap: 16,
                              minWidth: baseGridMinWidth
                            }}
                          >
                            {BASE_DEVICE_COLUMNS.map((col) => (
                              <div key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                                {t(col.labelKey)}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ width: 50, flexShrink: 0 }}>&nbsp;</div>
                      </div>

                      {placesWithDevices.map((place) =>
                        renderPlaceBlock(place, BASE_DEVICE_COLUMNS, baseGridTemplate, baseGridMinWidth, 'devices')
                      )}
                    </Grid>
                  )}

                  {/* Tabuľka nainštalovaných zariadení — pri "Zariadenie už mám nainštalované" vždy (nahrádza
                  tabuľku vyššie); pri rezervačnom toku sa zobrazí navyše, len ak niektoré miesto bolo doplnené
                  cez "Doplniť inštalované zariadenia" (conf-reserv tok) — vtedy existujú obe tabuľky naraz. */}
                  {showInstalledSection && (
                    <Grid
                      item
                      xs={12}
                      className={!showInstalledDeviceColumns ? 'mt-3' : ''}
                      style={{ border: '3px solid lightgrey', borderRadius: 5 }}
                    >
                      <div className='bold pt-1 pb-1' style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 300, flexShrink: 0 }}>
                          {showInstalledDeviceColumns
                            ? 'Miesto realizácie projektu'
                            : 'Miesto realizácie projektu — nainštalované zariadenia'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: installedGridTemplate,
                              gap: 16,
                              minWidth: installedGridMinWidth
                            }}
                          >
                            {INSTALLED_DEVICE_COLUMNS.map((col) => (
                              <div key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                                {t(col.labelKey)}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ width: 50, flexShrink: 0 }}>&nbsp;</div>
                      </div>

                      {placesWithInstalled.map((place) =>
                        renderPlaceBlock(
                          place,
                          INSTALLED_DEVICE_COLUMNS,
                          installedGridTemplate,
                          installedGridMinWidth,
                          'installed'
                        )
                      )}
                    </Grid>
                  )}

                  {showInstalledSection && (
                    <Grid item xs={12} className='mb-4'>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant='h6'>{t('request_form.installed_attachments_section')}</Typography>
                        </Grid>
                        {INSTALLED_ATTACHMENT_KEYS.map((key) => (
                          <Grid item xs={6} container key={key} spacing={1}>
                            <Grid item xs={12}>
                              <Typography variant='body1' className='bold'>
                                {t(INSTALLED_ATTACHMENT_LABELS[key])}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <XsDropzone
                                name={`dropzone_${key}`}
                                disabled={readonly && !canAddInstalledDevices}
                                multiple={INSTALLED_ATTACHMENT_MAX_FILES[key] > 1}
                                maxFiles={INSTALLED_ATTACHMENT_MAX_FILES[key]}
                                accept={['.pdf', '.xls', '.xlsx', '.odf', '.ods', '.odt']}
                                value={RequestFormStore.attachments[key]}
                                onChange={(files) => RequestFormStore.setAttachments(key, files as IAttachment[])}
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Controller
                      name='contributionForEnergyAudit'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          label={t('request_form.ea_grant')}
                          onChange={() => DeviceDialogStore.computeAssumedValue()}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {currentStep === 2 && (
              <>
                <Grid container spacing={2} className='mt-1'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>{t('request_form.section_minimal_support')}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={2} className='mt-1 ml-1'>
                  <Grid item xs={12}>
                    <Typography variant='body2'>{t('request_form.required_note')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body1' className='bold'>
                      Žiadateľ vyhlasuje, že:
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body1' className='bold' sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Tooltip
                        title={
                          <span style={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                            {`Jediný podnik zahŕňa všetky subjekty vykonávajúce hospodársku činnosť, medzi ktorými je aspoň jeden z týchto vzťahov:

a) jeden subjekt vykonávajúci hospodársku činnosť má väčšinu hlasovacích práv, ktoré patria akcionárom alebo spoločníkom v inom subjekte vykonávajúcom hospodársku činnosť;

b) jeden subjekt vykonávajúci hospodársku činnosť má právo vymenovať alebo odvolať väčšinu členov správneho, riadiaceho alebo dozorného orgánu iného subjektu vykonávajúceho hospodársku činnosť;

c) jeden subjekt vykonávajúci hospodársku činnosť má právo uplatňovať rozhodujúci vplyv na iný subjekt na základe zmluvy, ktorú s daným subjektom vykonávajúcim hospodársku činnosť uzavrel, alebo na základe ustanovenia v zakladajúcom dokumente alebo stanovách tohto subjektu;

d) jeden subjekt vykonávajúci hospodársku činnosť, ktorý je akcionárom alebo spoločníkom iného subjektu vykonávajúceho hospodársku činnosť, má sám na základe zmluvy s inými akcionármi alebo spoločníkmi daného subjektu vykonávajúceho hospodársku činnosť pod kontrolou väčšinu hlasovacích práv akcionárov alebo spoločníkov v danom subjekte vykonávajúcom hospodársku činnosť.

Subjekty vykonávajúce hospodársku činnosť, ktoré majú akýkoľvek zo vzťahov uvedených v písm. a) až d) tohto odseku prostredníctvom jedného alebo viacerých iných subjektov, sa takisto považujú za jediný podnik.`}
                          </span>
                        }
                        placement='top-start'
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 900,
                              width: 'max-content',
                              whiteSpace: 'pre-line',
                              lineHeight: 1.5,
                              padding: 1.5
                            }
                          }
                        }}
                      >
                        <span style={{ display: 'inline-flex', marginLeft: 8, cursor: 'help' }}>3.1 Jediný podnik</span>
                      </Tooltip>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='singleEnterpriseNo'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          onChange={(checked) => {
                            field.onChange(checked);
                            if (checked) setValue('singleEnterpriseYes', false);
                          }}
                          label={
                            <span>
                              <strong>{t('request_form.single_enterprise_no_bold')}</strong>
                              {t('request_form.single_enterprise_no_text')}
                            </span>
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='singleEnterpriseYes'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          onChange={(checked) => {
                            field.onChange(checked);
                            if (checked) setValue('singleEnterpriseNo', false);
                          }}
                          label={
                            <span>
                              <strong>{t('request_form.single_enterprise_yes_bold')}</strong>
                              {t('request_form.single_enterprise_yes_text')}
                            </span>
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container direction='column' spacing={1}>
                      {rpoSearchCompanies.map((entry) => (
                        <Grid item container spacing={1} alignItems='center' key={entry.id}>
                          <Grid item xs={12} md={6} lg={4}>
                            <XsAutocompleteServerWithoutLoad
                              name={`rpo_search_companies_${entry.id}`}
                              value={entry.value}
                              onChange={() => {}}
                              onBlur={() => {}}
                              disabled={readonly}
                              label={t('register.rpo_search_label')}
                              minLengthForSearch={3}
                              request={{
                                uri: RPO_SEARCH_URL,
                                includeToken: false,
                                payload: {
                                  search: (v: string) => getFilters([`search=${v}`], { row_count_full: 200 })
                                }
                              }}
                              identFn={(o: any) => o.identifier}
                              labelFn={(o: any) => (o ? `${o.name} ${o.identifier}` : '')}
                              onApply={(o: any) =>
                                setRpoSearchCompanies((prev) =>
                                  prev.map((e) => (e.id === entry.id ? { ...e, value: o ?? null } : e))
                                )
                              }
                            />
                          </Grid>
                          {/* kôš sa skryje, keď je len jeden (povinný) záznam */}
                          {!readonly && rpoSearchCompanies.length > 1 && (
                            <Grid item>
                              <XsIconButton
                                icon={<DeleteIcon />}
                                onClick={() =>
                                  setRpoSearchCompanies((prev) =>
                                    prev.length > 1 ? prev.filter((e) => e.id !== entry.id) : prev
                                  )
                                }
                              />
                            </Grid>
                          )}
                        </Grid>
                      ))}
                      {!readonly && (
                        <Grid item>
                          <XsButton
                            fullWidth={false}
                            variant='outlined'
                            label='request_form.rpo_search_add'
                            icon={<i className='fal fa-plus' />}
                            onClick={() =>
                              setRpoSearchCompanies((prev) => [...prev, { id: rpoSearchNextId.current++, value: null }])
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* ── 3.2 Neoprávnené vrátenie pomoci ── */}
                  <Grid item xs={12} className='mt-2'>
                    <Typography variant='body1' className='bold'>
                      3.2 {t('request_form.section_illegal_aid_return')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='illegalAidReturn'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          label={t('request_form.illegal_aid_return_label')}
                        />
                      )}
                    />
                  </Grid>

                  {/* ── 3.3 Vylúčené oblasti a sektory ── */}
                  <Grid item xs={12} className='mt-2'>
                    <Typography variant='body1' className='bold'>
                      3.3 {t('request_form.section_excluded_sectors')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='excludedSectorsNo'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          onChange={(checked) => {
                            field.onChange(checked);
                            if (checked) setValue('excludedSectorsYes', false);
                          }}
                          label={t('request_form.excluded_sectors_no_label')}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='excludedSectorsYes'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          onChange={(checked) => {
                            field.onChange(checked);
                            if (checked) setValue('excludedSectorsNo', false);
                          }}
                          label={t('request_form.excluded_sectors_yes_label')}
                        />
                      )}
                    />
                  </Grid>

                  {/* ── 3.4 Iná minimálna pomoc ── */}
                  <Grid item xs={12} className='mt-2'>
                    <Typography variant='body1' className='bold'>
                      3.4 {t('request_form.section_other_min_aid')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='otherMinAidNo'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          onChange={(checked) => {
                            field.onChange(checked);
                            if (checked) setValue('otherMinAidYes', false);
                          }}
                          label={
                            <span>
                              <strong>{t('request_form.other_min_aid_no_bold')}</strong>
                              {t('request_form.other_min_aid_no_text')}
                            </span>
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='otherMinAidYes'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          onChange={(checked) => {
                            field.onChange(checked);
                            if (checked) setValue('otherMinAidNo', false);
                          }}
                          label={
                            <span>
                              <strong>{t('request_form.other_min_aid_yes_bold')}</strong>
                              {t('request_form.other_min_aid_yes_text')}
                            </span>
                          }
                        />
                      )}
                    />
                  </Grid>

                  {/* ── 3.4 Iná minimálna pomoc — dynamický zoznam poskytovateľov (prvý riadok vždy viditeľný) ── */}
                  <Grid item xs={12}>
                    <Grid container direction='column' spacing={1}>
                      {otherMinAidEntries.map((entry) => (
                        <Grid item container spacing={2} alignItems='center' key={entry.id}>
                          <Grid item xs={12} md={4}>
                            <XsTextField
                              name={`other_min_aid_provider_${entry.id}`}
                              value={entry.provider}
                              disabled={readonly}
                              label={t('request_form.other_min_aid_provider')}
                              onChange={(e) =>
                                setOtherMinAidEntries((prev) =>
                                  prev.map((x) => (x.id === entry.id ? { ...x, provider: e.target.value } : x))
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <XsNumberField
                              name={`other_min_aid_amount_${entry.id}`}
                              value={entry.amount}
                              disabled={readonly}
                              label={t('request_form.other_min_aid_amount')}
                              onChange={(e) =>
                                setOtherMinAidEntries((prev) =>
                                  prev.map((x) => (x.id === entry.id ? { ...x, amount: e.target.value } : x))
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <XsDatePicker
                              name={`other_min_aid_date_${entry.id}`}
                              value={entry.date}
                              disabled={readonly}
                              label={t('request_form.other_min_aid_date')}
                              onChange={(date) =>
                                setOtherMinAidEntries((prev) =>
                                  prev.map((x) => (x.id === entry.id ? { ...x, date: date as Date | null } : x))
                                )
                              }
                            />
                          </Grid>
                          {/* kôš sa skryje, keď je len jeden (povinný) záznam */}
                          {!readonly && otherMinAidEntries.length > 1 && (
                            <Grid item>
                              <XsIconButton
                                icon={<DeleteIcon />}
                                onClick={() =>
                                  setOtherMinAidEntries((prev) =>
                                    prev.length > 1 ? prev.filter((x) => x.id !== entry.id) : prev
                                  )
                                }
                              />
                            </Grid>
                          )}
                        </Grid>
                      ))}
                      {!readonly && (
                        <Grid item>
                          <XsButton
                            fullWidth={false}
                            variant='outlined'
                            label='request_form.other_min_aid_add'
                            icon={<i className='fal fa-plus' />}
                            onClick={() =>
                              setOtherMinAidEntries((prev) => [
                                ...prev,
                                { id: otherMinAidNextId.current++, provider: '', amount: '', date: null }
                              ])
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            {currentStep === 3 && (
              <>
                {/* ───────── Čestné vyhlásenie ───────── */}
                <Grid container spacing={2} className='mt-4'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>{t('request_form.section_affidavit')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2'>{t('request_form.required_note')}</Typography>
                  </Grid>

                  {/* Sivá zóna s vyhláseniami */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #d0d0d0',
                        borderRadius: 1,
                        p: 3
                      }}
                    >
                      <Typography variant='body1' className='bold' sx={{ mb: 2 }}>
                        {t('request_form.affidavit_section_title')}
                      </Typography>
                      <ul style={{ paddingLeft: 20, margin: 0 }}>
                        {[
                          'affidavit_item_1',
                          'affidavit_item_2',
                          'affidavit_item_3',
                          'affidavit_item_4',
                          'affidavit_item_5',
                          'affidavit_item_6',
                          'affidavit_item_7'
                        ].map((key) => (
                          <li key={key} style={{ marginBottom: 8 }}>
                            <Typography variant='body2'>{t(`request_form.${key}`)}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  </Grid>

                  {/* Podmienky podpory */}
                  <Grid item xs={12} className='mt-2'>
                    <Typography variant='body1' sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                      {t('request_form.support_conditions_title')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name='consentConditions'
                      control={control}
                      render={({ field }) => (
                        <XsCheckbox
                          controllerProps={field}
                          disabled={readonly}
                          label={t('request_form.support_conditions_checkbox')}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {currentStep === 4 && (
              <>
                {/* ───────── 3. Prílohy ───────── */}
                <Grid container spacing={2} className='mt-4'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>{t('request_form.section_attachments')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2'>{t('request_form.attachments_note')}</Typography>
                  </Grid>
                  <Grid item container>
                    {ATTACHMENT_KEYS.map((key) => {
                      const isPoa = key === 'GD:GDREQ:POWEROFATTORNEY';
                      const isOptional = key === 'GD:GDREQ:DIVISIONACTIVITY';
                      const poaRequired = isPoa && !!authorizedPersonName;
                      const bold = isOptional ? false : isPoa ? poaRequired : true;
                      return (
                        <Grid item container spacing={1} key={key} style={{ marginBottom: 20 }}>
                          <Grid item xs={12}>
                            <Typography variant='body1' className={bold ? 'bold' : ''}>
                              {t(ATTACHMENT_LABELS[key])}
                              {bold ? ' *' : ''}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <XsDropzone
                              name={`dropzone_${key}`}
                              disabled={readonly}
                              accept={['.pdf', '.xls', '.xlsx', '.odf', '.ods', '.odt']}
                              value={RequestFormStore.attachments[key]}
                              onChange={(files) => RequestFormStore.setAttachments(key, files as IAttachment[])}
                            />
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </>
            )}

            {currentStep === 5 && (
              <>
                {/* ───────── 4. Predpokladaná hodnota poukážky ───────── */}
                <Grid container className='mt-8'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>{t('request_form.section_voucher')}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2' className='redDanger mt-2'>
                      {t('request_form.voucher_warning')}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className='mt-8' justifyContent='center'>
                  <Grid item xs={12} md={6} lg={4} style={{ border: '1px solid #CED7D9', borderRadius: 5 }}>
                    <Typography variant='h3' className='textCenter pt-1 pb-1'>
                      {eur(voucherAmount)}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}

            {currentStep === 6 && (
              <>
                {/* ───────── 4. Predpokladaná hodnota poukážky ───────── */}
                <Grid container className='mt-8'>
                  <Grid item xs={12}>
                    <Typography variant='h5'>{t('request_form.section_summary')}</Typography>
                  </Grid>
                </Grid>
                <Grid container className='mt-8' justifyContent='center' alignItems='center'>
                  <Grid item xs={6}>
                    <Typography variant='body1'>{t('request_form.expected_voucher_amount')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='h5' className='pt-1 pb-1'>
                      {eur(voucherAmount)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container className='mt-8' justifyContent='center' alignItems='center'>
                  <Grid item xs={6}>
                    <Typography variant='body1'>{t('request_form.device_label')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {summaryDeviceLabel}
                  </Grid>
                </Grid>
                <Grid container justifyContent='center' alignItems='center'>
                  <Grid item xs={6}>
                    <Typography variant='body1'>{t('request_form.power_label')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {summaryPowerLabel}
                  </Grid>
                </Grid>
                <Grid container justifyContent='center' alignItems='center'>
                  <Grid item xs={6}>
                    <Typography variant='body1'>{t('request_form.energy_audit_label')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {summaryEnergyAuditLabel}
                  </Grid>
                </Grid>
                <Grid container justifyContent='center' alignItems='center'>
                  <Grid item xs={6}>
                    <Typography variant='body1'>{t('request_form.applicant_name_label')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {summaryApplicantName}
                  </Grid>
                </Grid>
                <Grid container justifyContent='center' alignItems='center'>
                  <Grid item xs={6}>
                    <Typography variant='body1'>{t('request_form.applicant_ico_label')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {summaryApplicantIco}
                  </Grid>
                </Grid>
                <Grid container justifyContent='center' alignItems='center'>
                  <Grid item xs={6}>
                    <Typography variant='body1'>{t('request_form.installation_location_label')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {summaryLocationLabel}
                  </Grid>
                </Grid>
                <Grid container justifyContent='center' className='mb-8 mt-8'>
                  <Grid item>
                    <XsButton
                      fullWidth={false}
                      color='success'
                      label='request_form.send_request'
                      icon={<i className='fal fa-paper-plane' />}
                      onClick={() => openConsent('sent')()}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {/* ───────── Spodné akcie ───────── */}
            <Box className='request_form_buttons' style={{ marginTop: 15 }}>
              <Grid
                container
                spacing={1}
                alignItems='center'
                justifyContent='space-between'
                style={{ paddingLeft: 16, paddingRight: 16 }}
              >
                <Grid item>
                  <XsButton
                    fullWidth={false}
                    variant='outlined'
                    label='request_form.back_to_list'
                    icon={<i className='fas fa-arrow-left' />}
                    onClick={() => history.push('/requests')}
                  />
                </Grid>
                <Grid item>
                  <Grid container spacing={1}>
                    {RequestFormStore.showEditButton && currentStep < STEP_COUNT - 1 && (
                      <Grid item>
                        <XsButton
                          fullWidth={false}
                          label={readonly ? t('request_form.edit') : t('request_form.discard_changes')}
                          icon={<i className='fal fa-pencil-alt' />}
                          onClick={() => RequestFormStore.setReadonly(!readonly)}
                        />
                      </Grid>
                    )}
                    {RequestFormStore.requestInProgress && currentStep < STEP_COUNT - 1 && (
                      <Grid item>
                        <XsButton
                          fullWidth={false}
                          variant='outlined'
                          label='request_form.save_draft'
                          icon={<i className='fal fa-save' />}
                          onClick={() => openConsent('inp')()}
                        />
                      </Grid>
                    )}
                    {currentStep < STEP_COUNT - 1 && (
                      <Grid item>
                        <XsButton
                          fullWidth={false}
                          label='request_form.save_and_continue'
                          icon={<i className='fal fa-save' />}
                          onClick={() => {
                            if (RequestFormStore.requestInProgress) {
                              continueAfterSaveRef.current = true;
                              openConsent('inp')();
                            } else {
                              RequestFormStepperStore.next();
                            }
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </form>
        </FormProvider>
      </Container>

      <DeviceDialog />
      {/* Editácia už pridaného inštalovaného zariadenia v tabuľke miest (tlačidlo "pen" — viď
      openEditInstalledDevice); po potvrdení nahradí pôvodný záznam (viď handleEditInstalledDeviceConfirm) */}
      <InstalledDeviceSelectDialog
        open={!!editInstalledTarget}
        onClose={() => setEditInstalledTarget(null)}
        onConfirm={handleEditInstalledDeviceConfirm}
        editInitial={editInstalledInitial}
      />
      <ConsentDialog onConfirm={onConfirmed} />
      <WarningDialog />
    </>
  );
}

export default observer(RequestForm);
